import { storyblokEditable } from '@storyblok/react/rsc';
import { ArrowRight } from '@phosphor-icons/react/dist/ssr';
import { resolveAsset, resolveLink, optimizeImage, srcSet } from '@/lib/helpers';
import CmsButton from '@/components/CmsButton';

// Campaign hero from the 2026 marketing designs: copy on a solid colour on the
// left, a photo on the right with a discount badge, a stat card and a
// notification floating over its edge. The animation (styles/promo-hero.css)
// follows the Figma prototype's 6s timeline: the headline types in letter by
// letter, then the badge, card, toast and subline pop in after it.

// The headline takes this long to type in, however many letters it has.
const TYPE_MS = 2600;

// One span per letter so each can fade in on its own delay. Words stay in a
// no-wrap span so a line never breaks mid-word. Screen readers get the plain
// headline from aria-label instead of a letter-by-letter reading.
function TypedHeadline({ text }) {
  const words = text.split(/(\s+)/);
  const count = text.replace(/\s/g, '').length || 1;
  const step = TYPE_MS / count;
  let i = 0;
  return words.map((word, w) => (/^\s+$/.test(word) ? word : (
    <span key={w} className="inline-block whitespace-nowrap" aria-hidden="true">
      {[...word].map((ch, c) => (
        <span key={c} className="promo-char" style={{ '--d': `${Math.round(i++ * step)}ms`, '--len': `${Math.round(step)}ms` }}>{ch}</span>
      ))}
    </span>
  )));
}

const PromoHero = ({ blok }) => {
  const imageSrc = resolveAsset(blok.image);
  const bg = blok.background_color || '#610007';
  const fg = blok.text_color || '#FFE5E7';
  const motion = blok.animation || 'once';
  const hasStat = blok.stat_label || blok.stat_value || blok.stat_note;

  return (
    <section
      {...storyblokEditable(blok)}
      className={`promo-hero promo-${motion} rounded-t-[30px] max-w-hero mx-4 mt-2 overflow-hidden`}
      style={{ backgroundColor: bg, color: fg }}
    >
      <div className="promo-row flex flex-col sm:flex-row">
        <div className="promo-copy flex-1 flex items-center px-6 py-10">
          <div className="promo-copy-inner flex flex-col gap-8 w-full">
            <div className="promo-text flex flex-col gap-4">
              {blok.title && (
                <h1 className="promo-title font-extrabold leading-none" aria-label={blok.title}>
                  <TypedHeadline text={blok.title} />
                </h1>
              )}
              {blok.subtitle && (
                <p className="promo-subtitle text-18 leading-[1.3]">{blok.subtitle}</p>
              )}
            </div>
            {blok.cta_text && (blok.cta_style ? (
              <CmsButton label={blok.cta_text} link={blok.cta_link} style={blok.cta_style} showArrow={blok.cta_arrow} />
            ) : (
              // Default button: the hero's own colours swapped, as in the design.
              <a href={resolveLink(blok.cta_link)} className="btn w-fit semi-lg:py-6" style={{ backgroundColor: fg, color: bg }}>
                {blok.cta_text}
                {blok.cta_arrow && <ArrowRight size={24} weight="light" />}
              </a>
            ))}
          </div>
        </div>

        <div className="promo-visual relative h-[420px] tiny:h-[460px] sm:h-auto sm:w-[44.9%] sm:shrink-0">
          {imageSrc && (
            <img
              src={optimizeImage(imageSrc, 1300, 0)}
              srcSet={srcSet(imageSrc)}
              sizes="(max-width: 767px) 100vw, 45vw"
              alt={blok.image?.alt || ''}
              className="absolute inset-0 w-full h-full object-cover"
              style={blok.image?.focus ? { objectPosition: focusPosition(blok.image) } : undefined}
              fetchPriority="high"
            />
          )}

          {blok.badge_text && (
            <div className="promo-badge promo-pop" aria-hidden="true">
              <div className="promo-badge-disc" style={{ backgroundColor: blok.badge_color || '#FF0014' }}>
                {blok.badge_ring_text && <BadgeRing text={blok.badge_ring_text} />}
                <span className="promo-badge-text font-extrabold">{blok.badge_text}</span>
              </div>
            </div>
          )}
          {blok.badge_text && (
            <span className="sr-only">{[blok.badge_text, blok.badge_ring_text].filter(Boolean).join(' ')}</span>
          )}

          {hasStat && (
            <div className="promo-stat promo-pop">
              <div className="promo-float" style={{ backgroundColor: fg, color: bg }}>
                {blok.stat_label && <p className="promo-stat-label">{blok.stat_label}</p>}
                {blok.stat_value && <p className="promo-stat-value font-bold">{blok.stat_value}</p>}
                {blok.stat_note && (
                  <p className="promo-stat-label" style={{ color: blok.stat_note_color || '#93000C' }}>{blok.stat_note}</p>
                )}
              </div>
            </div>
          )}

          {blok.toast_text && (
            <div className="promo-toast promo-pop">
              <p className="promo-float text-black whitespace-nowrap">{blok.toast_text}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// The ring text repeated round the badge's edge, three times, as in the design.
// Drawn on an SVG circle path so it stays a single editable line of text.
function BadgeRing({ text }) {
  // A plain trailing space would be collapsed away, running the repeats together.
  const label = `${text.trim()}\u00A0`.toUpperCase();
  return (
    <svg className="promo-badge-ring" viewBox="0 0 219 219" aria-hidden="true">
      <defs>
        <path id="promo-ring" d="M109.5,109.5 m-90,0 a90,90 0 1,1 180,0 a90,90 0 1,1 -180,0" />
      </defs>
      <text fill="currentColor" fontSize="16" fontWeight="700" letterSpacing="1.6">
        <textPath href="#promo-ring" textLength="565" lengthAdjust="spacing">{label.repeat(3)}</textPath>
      </text>
    </svg>
  );
}

// Storyblok stores the editor's focus point as "x1xy1:x2xy2" in pixels; turn
// its centre into an object-position so cropping keeps the subject in frame.
function focusPosition(image) {
  const m = image.focus.match(/^(\d+)x(\d+):(\d+)x(\d+)$/);
  const size = image.filename?.match(/\/(\d+)x(\d+)\//);
  if (!m || !size) return undefined;
  const x = ((+m[1] + +m[3]) / 2 / +size[1]) * 100;
  const y = ((+m[2] + +m[4]) / 2 / +size[2]) * 100;
  return `${x.toFixed(1)}% ${y.toFixed(1)}%`;
}

export default PromoHero;
